import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Search, RefreshCw, Check, X, Star, Calendar, ExternalLink, Plus, Home, Bed, Bath, MapPin, Edit3, Lock } from 'lucide-react';
import { notifyError, notifySuccess } from '../../lib/notify';
import SubmitHousingModal from '../../components/SubmitHousingModal';
import SubmitEventModal from '../../components/SubmitEventModal';
import EditEventModal from '../../components/admin/EditEventModal';

const API = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_OPTIONS = [
  { value: 'pending',  label: 'Pending',  tone: 'bg-[#E8A830]/15 text-[#8A6310] border-[#E8A830]/40' },
  { value: 'approved', label: 'Approved', tone: 'bg-[#2CA8A0]/15 text-[#1A5C6E] border-[#2CA8A0]/40' },
  { value: 'rejected', label: 'Rejected', tone: 'bg-[#B91C1C]/10 text-[#7F1414] border-[#B91C1C]/40' },
];

const fmtDate = (iso) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'America/Chicago'});
  } catch { return iso; }
};

const StatusPill = ({ status, statusOptions }) => {
  const list = statusOptions || STATUS_OPTIONS;
  const meta = list.find((s) => s.value === status) || list[0];
  return <span className={`inline-block text-[10px] font-mono uppercase tracking-[0.1em] px-2 py-1 rounded-full border ${meta.tone}`}>{meta.label}</span>;
};

const StarRow = ({ value }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star key={n} className={`w-3.5 h-3.5 ${n <= value ? 'fill-[#E8A830] text-[#E8A830]' : 'text-[#0A1F2E]/15'}`} />
    ))}
  </span>
);

/**
 * Reusable admin moderation page. Pass `kind="reviews"` or `kind="events"`.
 * The component handles both surfaces because they have identical workflow
 * (list → approve / reject → optional reason).
 */
export const AdminModerationPage = ({ kind, title, eyebrow, listEndpoint, moderateEndpointTpl, columns, renderRow, statusKey = 'status', statusOptions, headerActions, extraPendingActions }) => {
  const STATUS_LIST = statusOptions || STATUS_OPTIONS;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [busyId, setBusyId] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}${listEndpoint}?status=${statusFilter}&limit=500`, { credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        setItems(d.submissions || d.reviews || []);
      } else if (r.status === 401 || r.status === 403) {
        notifyError('Admin session expired. Please log in again.');
      } else {
        notifyError(await r.json().catch(() => ({})), 'Could not load submissions.');
      }
    } catch { notifyError('Network error.'); }
    setLoading(false);
  }, [listEndpoint, statusFilter]);

  useEffect(() => { reload(); }, [reload]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => JSON.stringify(i).toLowerCase().includes(q));
  }, [items, search]);

  const moderate = async (id, action) => {
    let reason;
    if (action === 'reject') {
      reason = window.prompt('Reason (optional, shown to the submitter):') || '';
    }
    setBusyId(id);
    try {
      const r = await fetch(`${API}${moderateEndpointTpl(id)}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, rejection_reason: reason }),
      });
      if (r.ok) {
        notifySuccess(action === 'approve' ? 'Approved.' : 'Rejected.');
        await reload();
      } else {
        notifyError(await r.json().catch(() => ({})), 'Could not update.');
      }
    } catch { notifyError('Network error.'); }
    setBusyId(null);
  };

  return (
    <div className="min-h-screen bg-[#F9F6EF]" data-testid={`admin-${kind}-page`}>
      <header className="border-b border-[#EFE6CF] bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-5 flex items-center gap-4">
          <Link to="/admin/dashboard" className="text-[#0A1F2E]/55 hover:text-[#0A1F2E]" data-testid={`admin-${kind}-back`}>
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/55">Mundalo · Admin</p>
            <h1 className="font-display italic text-2xl text-[#0A1F2E]">{title}</h1>
          </div>
          <button type="button" onClick={reload} className="ml-auto inline-flex items-center gap-2 text-[#1A5C6E] hover:text-[#0A1F2E] font-mono uppercase text-[10px] tracking-[0.15em] px-3 py-2 rounded-full border border-[#EFE6CF] hover:border-[#0A1F2E]/30 transition" data-testid={`admin-${kind}-refresh`}>
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          {headerActions && headerActions({ reload })}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="bg-white border border-[#EFE6CF] rounded-2xl p-4 mb-6 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#0A1F2E]/40" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search…"
              className="w-full bg-[#F9F6EF] border border-[#EFE6CF] rounded-full pl-10 pr-4 py-2 text-sm text-[#0A1F2E] focus:outline-none focus:ring-2 focus:ring-[#2CA8A0]/40"
              data-testid={`admin-${kind}-search`} />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-white border border-[#EFE6CF] rounded-full px-4 py-2 text-sm text-[#0A1F2E] focus:outline-none focus:ring-2 focus:ring-[#2CA8A0]/40"
            data-testid={`admin-${kind}-filter-status`}>
            {STATUS_LIST.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="text-center py-20 text-[#0A1F2E]/55"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : filtered.length === 0 ? (
          <p className="text-center py-20 text-[#0A1F2E]/55" data-testid={`admin-${kind}-empty`}>
            {items.length === 0 ? `No ${kind} match this status.` : 'No matches for this search.'}
          </p>
        ) : (
          <div className="space-y-4">
            {filtered.map((row) => (
              <div key={row.id} className="bg-white border border-[#EFE6CF] rounded-2xl p-5" data-testid={`admin-${kind}-row-${row.id}`}>
                {renderRow(row)}
                <div className="mt-4 pt-4 border-t border-[#EFE6CF] flex items-center justify-between gap-3">
                  <StatusPill status={row[statusKey]} statusOptions={STATUS_LIST} />
                  {row[statusKey] === 'pending' && (
                    <div className="flex gap-2">
                      {extraPendingActions && extraPendingActions({ row, reload })}
                      <button type="button" disabled={busyId === row.id}
                        onClick={() => moderate(row.id, 'reject')}
                        className="inline-flex items-center gap-1.5 text-[#7F1414] hover:bg-[#B91C1C]/10 border border-[#B91C1C]/30 rounded-full px-4 py-1.5 font-mono uppercase text-[10px] tracking-[0.15em] transition disabled:opacity-50"
                        data-testid={`admin-${kind}-reject-${row.id}`}>
                        <X className="w-3.5 h-3.5" /> Reject
                      </button>
                      <button type="button" disabled={busyId === row.id}
                        onClick={() => moderate(row.id, 'approve')}
                        className="inline-flex items-center gap-1.5 bg-[#0A1F2E] hover:bg-[#1A5C6E] text-white rounded-full px-4 py-1.5 font-mono uppercase text-[10px] tracking-[0.15em] transition disabled:opacity-50"
                        data-testid={`admin-${kind}-approve-${row.id}`}>
                        <Check className="w-3.5 h-3.5" /> Approve
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

// ---------------- EVENTS ----------------
// iter 142 - admin can now seed curated events directly via the "+ Create event"
// header button. Goes live immediately (status="approved"), parallels the
// housing direct-create pattern.
const AdminEventsHeaderActions = ({ onReload }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 bg-[#0A1F2E] hover:bg-[#1A5C6E] text-white font-mono uppercase text-[10px] tracking-[0.15em] px-3 py-2 rounded-full transition"
        data-testid="admin-events-create-btn"
      >
        <Plus className="w-3.5 h-3.5" /> Create event
      </button>
      <SubmitEventModal
        open={open}
        adminMode
        onClose={() => setOpen(false)}
        onSubmitted={onReload}
      />
    </>
  );
};

export const AdminEventsModeration = () => {
  // iter 142c - admin can EDIT a pending member submission before approving.
  // Held at this level so the modal can survive across list reloads.
  const [editing, setEditing] = useState(null);
  const reloadRef = useRef(null);
  return (
    <>
      <AdminModerationPage
        kind="events-moderation"
        title="Events · Moderation"
        listEndpoint="/api/admin/events/pending"
        moderateEndpointTpl={(id) => `/api/admin/events/${id}/moderate`}
        headerActions={({ reload }) => {
          reloadRef.current = reload;
          return <AdminEventsHeaderActions onReload={reload} />;
        }}
        extraPendingActions={({ row }) => (
          // Curated admin-direct rows skip the Edit button since they were
          // just written by the admin and don't need pre-publish revision.
          !row.created_by_admin && (
            <button
              type="button"
              onClick={() => setEditing(row)}
              className="inline-flex items-center gap-1.5 text-[#1A5C6E] hover:bg-[#2CA8A0]/10 border border-[#2CA8A0]/40 rounded-full px-4 py-1.5 font-mono uppercase text-[10px] tracking-[0.15em] transition"
              data-testid={`admin-events-edit-${row.id}`}
            >
              <Edit3 className="w-3.5 h-3.5" /> Edit
            </button>
          )
        )}
        renderRow={(row) => (
          <div className="grid md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <p className="font-mono uppercase tracking-[0.15em] text-[10px] text-[#1A5C6E] mb-1">
                {row.category} · {row.type}
                {row.created_by_admin && (
                  <span className="ml-2 text-[#E8A830]">ADMIN POST</span>
                )}
                {row.peer_circle_group_id && (
                  <span className="ml-2 inline-flex items-center gap-1 text-[#7F1414] bg-[#7F1414]/10 px-1.5 py-0.5 rounded">
                    <Lock className="w-3 h-3" /> PEER CIRCLE
                  </span>
                )}
              </p>
              <h3 className="font-display italic text-2xl text-[#0A1F2E] leading-tight mb-2">{row.title}</h3>
              <p className="text-sm text-[#0A1F2E]/85 mb-3">{row.summary}</p>
              <p className="text-sm text-[#0A1F2E]/75 whitespace-pre-line leading-relaxed">{row.description}</p>
              {row.url && (
                <a href={row.url} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-1 mt-3 text-[#1A5C6E] hover:text-[#0A1F2E] text-xs font-mono uppercase tracking-[0.12em]">
                  <ExternalLink className="w-3.5 h-3.5" /> Provided link
                </a>
              )}
              {row.edited_at && (
                <p className="mt-3 text-[10px] text-[#0A1F2E]/55 italic">
                  Edited by {row.edited_by} on {fmtDate(row.edited_at)}
                </p>
              )}
            </div>
            <aside className="bg-[#F9F6EF] border border-[#EFE6CF] rounded-xl p-4 text-sm space-y-2">
              <div>
                <p className="font-mono uppercase tracking-[0.15em] text-[10px] text-[#0A1F2E]/55 mb-0.5">When</p>
                <p className="text-[#0A1F2E] inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {fmtDate(row.when_utc)}</p>
                <p className="text-[10px] text-[#0A1F2E]/55 mt-0.5">{row.duration_minutes} minutes</p>
              </div>
              <div>
                <p className="font-mono uppercase tracking-[0.15em] text-[10px] text-[#0A1F2E]/55 mb-0.5">Where</p>
                <p className="text-[#0A1F2E]">{row.location}</p>
              </div>
              {row.city && (
                <div>
                  <p className="font-mono uppercase tracking-[0.15em] text-[10px] text-[#0A1F2E]/55 mb-0.5">City</p>
                  <p className="text-[#0A1F2E]">{row.city}</p>
                </div>
              )}
              <div>
                <p className="font-mono uppercase tracking-[0.15em] text-[10px] text-[#0A1F2E]/55 mb-0.5">Submitted by</p>
                <p className="text-[#0A1F2E]">{row.submitted_by_name}</p>
                <p className="text-[10px] text-[#0A1F2E]/55">{row.submitted_by_email}</p>
                <p className="text-[10px] text-[#0A1F2E]/55 mt-1">{fmtDate(row.created_at)}</p>
              </div>
            </aside>
          </div>
        )}
      />
      <EditEventModal
        open={!!editing}
        event={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); reloadRef.current?.(); }}
      />
    </>
  );
};

// ---------------- REVIEWS ----------------
export const AdminReviewsModeration = () => (
  <AdminModerationPage
    kind="reviews-moderation"
    title="Reviews · Moderation"
    listEndpoint="/api/admin/reviews/pending"
    moderateEndpointTpl={(id) => `/api/admin/reviews/${id}/moderate`}
    renderRow={(row) => (
      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3 mb-2">
            <StarRow value={row.rating} />
            <span className="font-mono uppercase tracking-[0.15em] text-[10px] text-[#1A5C6E]">{row.city}</span>
          </div>
          <h3 className="font-display italic text-2xl text-[#0A1F2E] leading-tight mb-2">"{row.headline}"</h3>
          <p className="text-sm text-[#0A1F2E]/85 leading-relaxed whitespace-pre-line">{row.body}</p>
        </div>
        <aside className="bg-[#F9F6EF] border border-[#EFE6CF] rounded-xl p-4 text-sm space-y-2">
          <div>
            <p className="font-mono uppercase tracking-[0.15em] text-[10px] text-[#0A1F2E]/55 mb-0.5">Submitted by</p>
            <p className="text-[#0A1F2E]">{row.submitted_by_name}</p>
            <p className="text-[10px] text-[#0A1F2E]/55">{row.submitted_by_email}</p>
            <p className="text-[10px] text-[#0A1F2E]/55 mt-1">{fmtDate(row.created_at)}</p>
          </div>
          {row.seeded && (
            <p className="text-[10px] font-mono uppercase tracking-[0.15em] text-[#E8A830]">SEED · placeholder</p>
          )}
        </aside>
      </div>
    )}
  />
);

const HOUSING_STATUS_OPTIONS = [
  { value: 'pending',  label: 'Pending',  tone: 'bg-[#E8A830]/15 text-[#8A6310] border-[#E8A830]/40' },
  { value: 'active',   label: 'Active',   tone: 'bg-[#2CA8A0]/15 text-[#1A5C6E] border-[#2CA8A0]/40' },
  { value: 'rejected', label: 'Rejected', tone: 'bg-[#B91C1C]/10 text-[#7F1414] border-[#B91C1C]/40' },
];

const LISTING_TYPE_LABEL = { rent: 'Rent', roommate: 'Roommate', sublet: 'Sublet' };

const fmtAvail = (s) => {
  if (!s) return '-';
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'America/Chicago'});
  } catch {
    return s;
  }
};

// Wrapper that injects the "+ Create listing" button into the moderation header.
const AdminHousingHeaderActions = ({ onReload }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-2 bg-[#0A1F2E] hover:bg-[#1A5C6E] text-white font-mono uppercase text-[10px] tracking-[0.15em] px-3 py-2 rounded-full transition"
        data-testid="admin-housing-create-btn"
      >
        <Plus className="w-3.5 h-3.5" /> Create listing
      </button>
      <SubmitHousingModal
        open={open}
        adminMode
        onClose={() => setOpen(false)}
        onSubmitted={onReload}
      />
    </>
  );
};

export const AdminHousingModeration = () => (
  <AdminModerationPage
    kind="housing-moderation"
    title="Housing · Listings"
    listEndpoint="/api/admin/housing/pending"
    moderateEndpointTpl={(id) => `/api/admin/housing/${id}/moderate`}
    statusOptions={HOUSING_STATUS_OPTIONS}
    headerActions={({ reload }) => <AdminHousingHeaderActions onReload={reload} />}
    renderRow={(row) => (
      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="bg-[#0A1F2E] text-white rounded-full font-mono uppercase text-[10px] tracking-[0.12em] px-2.5 py-1">
              {LISTING_TYPE_LABEL[row.listing_type] || row.listing_type}
            </span>
            <span className="inline-flex items-center gap-1 text-[#1A5C6E] font-mono uppercase tracking-[0.12em] text-[10px]">
              <MapPin className="w-3 h-3" /> {row.city}
            </span>
            {row.created_by_admin && (
              <span className="font-mono uppercase text-[10px] tracking-[0.12em] text-[#E8A830]">ADMIN POST</span>
            )}
          </div>
          <h3 className="font-display italic text-2xl text-[#0A1F2E] leading-tight mb-2">"{row.title}"</h3>
          <p className="text-sm text-[#0A1F2E]/85 leading-relaxed whitespace-pre-line mb-4">{row.description}</p>

          {row.photos?.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-1 mb-3">
              {row.photos.map((src, i) => (
                <img
                  key={src}
                  src={src}
                  alt={`Listing photo ${i + 1}`}
                  className="h-20 w-20 object-cover rounded-lg border border-[#EFE6CF] flex-shrink-0"
                  loading="lazy"
                />
              ))}
            </div>
          )}

          <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-[#0A1F2E]/70 font-mono">
            <span className="inline-flex items-center gap-1"><Home className="w-3 h-3" /> ${Number(row.price_monthly_usd || 0).toLocaleString('en-US', { timeZone: 'America/Chicago' })}/mo</span>
            <span className="inline-flex items-center gap-1"><Bed className="w-3 h-3" /> {row.bedrooms} bd</span>
            <span className="inline-flex items-center gap-1"><Bath className="w-3 h-3" /> {row.bathrooms} ba</span>
            <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> avail {fmtAvail(row.available_date)}</span>
          </div>
        </div>
        <aside className="bg-[#F9F6EF] border border-[#EFE6CF] rounded-xl p-4 text-sm space-y-2">
          <div>
            <p className="font-mono uppercase tracking-[0.15em] text-[10px] text-[#0A1F2E]/55 mb-0.5">Submitted by</p>
            <p className="text-[#0A1F2E]">{row.submitted_by_name}</p>
            <p className="text-[10px] text-[#0A1F2E]/55">{row.submitted_by_email}</p>
            <p className="text-[10px] text-[#0A1F2E]/55 mt-1">{fmtDate(row.created_at)}</p>
          </div>
          {row.rejection_reason && (
            <div className="pt-2 border-t border-[#EFE6CF]">
              <p className="font-mono uppercase tracking-[0.15em] text-[10px] text-[#7F1414] mb-0.5">Rejected</p>
              <p className="text-[12px] text-[#0A1F2E]/85">{row.rejection_reason}</p>
            </div>
          )}
        </aside>
      </div>
    )}
  />
);



// ---------------- Local Deal Submissions ----------------

const LOCAL_DEAL_STATUS_OPTIONS = HOUSING_STATUS_OPTIONS;  // identical 3 statuses

export const AdminLocalDealSubmissions = () => (
  <AdminModerationPage
    kind="local-deal-submissions"
    title="Local Deals · Submissions"
    listEndpoint="/api/admin/local-deals/pending"
    moderateEndpointTpl={(id) => `/api/admin/local-deals/submissions/${id}/moderate`}
    statusOptions={LOCAL_DEAL_STATUS_OPTIONS}
    renderRow={(row) => (
      <div className="grid md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <span className="bg-[#0A1F2E] text-white rounded-full font-mono uppercase text-[10px] tracking-[0.12em] px-2.5 py-1">
              {row.category}
            </span>
            <span className="inline-flex items-center gap-1 text-[#1A5C6E] font-mono uppercase tracking-[0.12em] text-[10px]">
              <MapPin className="w-3 h-3" /> {row.city}
            </span>
          </div>
          <h3 className="font-display italic text-2xl text-[#0A1F2E] leading-tight mb-1">"{row.name}"</h3>
          <p className="font-mono uppercase text-[10px] tracking-[0.12em] text-[#1A5C6E] mb-3">
            {row.discount}
          </p>
          <p className="text-sm text-[#0A1F2E]/85 leading-relaxed whitespace-pre-line mb-3">{row.description}</p>

          {row.image_url && (
            <img
              src={row.image_url}
              alt={`${row.name} logo`}
              className="h-20 w-20 object-cover rounded-lg border border-[#EFE6CF] mb-3"
              loading="lazy"
            />
          )}

          <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-[#0A1F2E]/70 font-mono">
            <span className="inline-flex items-center gap-1">
              <Calendar className="w-3 h-3" /> valid until {fmtAvail(row.valid_until)}
            </span>
            {row.url && (
              <a
                href={row.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[#1A5C6E] hover:text-[#0A1F2E]"
              >
                <ExternalLink className="w-3 h-3" /> link
              </a>
            )}
          </div>
        </div>
        <aside className="bg-[#F9F6EF] border border-[#EFE6CF] rounded-xl p-4 text-sm space-y-2">
          <div>
            <p className="font-mono uppercase tracking-[0.15em] text-[10px] text-[#0A1F2E]/55 mb-0.5">Submitted by</p>
            <p className="text-[#0A1F2E]">{row.submitter_name}</p>
            <p className="text-[10px] text-[#0A1F2E]/55">{row.submitter_email}</p>
            <p className="text-[10px] text-[#0A1F2E]/55 mt-1">{fmtDate(row.created_at)}</p>
          </div>
          <div className="pt-2 border-t border-[#EFE6CF] text-[11px] text-[#0A1F2E]/65">
            <span className="font-mono uppercase tracking-[0.15em] text-[10px] text-[#2CA8A0]">Survey opt-in</span> ·{' '}
            {row.survey_consent_at ? new Date(row.survey_consent_at).toLocaleDateString('en-US', { timeZone: 'America/Chicago' }) : '-'}
          </div>
          {row.rejection_reason && (
            <div className="pt-2 border-t border-[#EFE6CF]">
              <p className="font-mono uppercase tracking-[0.15em] text-[10px] text-[#7F1414] mb-0.5">Rejected</p>
              <p className="text-[12px] text-[#0A1F2E]/85">{row.rejection_reason}</p>
            </div>
          )}
        </aside>
      </div>
    )}
  />
);
